import React from 'react';
import { useMaybeRoomContext } from '@livekit/components-react';
import { RoomEvent, Track, TrackPublication } from 'livekit-client';
import { SourceSelectorModal } from '@/components/modals/source-selector-modal.tsx';
import { ScreenShareIcon, ScreenShareOffIcon } from 'lucide-react';
import { ActionTooltip } from '@/components/action-tooltip.tsx';
import { ScreenShareVideoEncodings } from '@/context/call-context/screen-share-options.ts';
import { cn } from '@heroui/theme';
import { useAuth } from '@/context/auth-context/use-auth.ts';
import { useSocket } from '@/context/socket-context/use-external-socket.ts';
import { useCall } from '@/context/call-context/use-call.ts';

export const ScreenShareToggle: React.FC<{
  captureSystemAudio?: boolean;
  docked?: boolean;
}> = ({ captureSystemAudio = false, docked }) => {
  const room = useMaybeRoomContext();

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const handleOpen = () => setIsModalOpen(true);
  const handleClose = () => setIsModalOpen(false);
  const { user } = useAuth();
  const socket = useSocket();
  const { activeCall, screenShare } = useCall();
  const isSharing = screenShare.isSharing;

  const handleSelect = async (source: any) => {
    handleClose();

    if (isSharing) await stopSharing();

    try {
      let stream: MediaStream | null = source.stream;
      if (!stream) {
        stream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            frameRate: { ideal: source.fps ?? 60, max: source.fps ?? 60 },
            width: { ideal: source.resolution?.width ?? 1920 },
            height: { ideal: source.resolution?.height ?? 1080 },
          },
          audio: captureSystemAudio,
        });
      }

      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack: MediaStreamTrack | undefined =
        stream.getAudioTracks()[0];

      if (!videoTrack) return;
      const settings = videoTrack.getSettings();
      console.log('captured screen settings', settings);

      const fps = source.fps ?? settings.frameRate ?? 30;
      const height = settings.height ?? source.resolution?.height ?? 1080;

      const maxBitrate =
        height >= 2160
          ? 16_000_000
          : height >= 1080
            ? 8_000_000
            : height >= 720
              ? 4_000_000
              : 2_000_000;

      await room?.localParticipant.publishTrack(videoTrack, {
        name: 'screen',
        source: Track.Source.ScreenShare,
        videoEncoding: {
          maxBitrate,
          maxFramerate: fps,
        },
        // Для диагностики можно временно включить/выключить simulcast.
        // simulcast: true,
        degradationPreference: 'maintain-resolution',
      });

      if (audioTrack) {
        await room?.localParticipant.publishTrack(audioTrack, {
          name: 'system-audio',
          source: Track.Source.ScreenShareAudio,
          dtx: false,
          red: false,
        });
      }

      videoTrack.onended = stopSharing;
      if (audioTrack) audioTrack.onended = stopSharing;

      if (!socket || !user?.id) return;
      
      const screenSharingMessage = JSON.stringify({
        type: 'screen-sharing',
        meta: {
          channelId: activeCall.chatId,
          userId: user.id,
          participantIdentity: activeCall.name || user.name || user.id,
          deviceId: videoTrack.id,
        },
      });
      socket.send(screenSharingMessage);
    } catch (err) {
      console.error('Error starting screen share:', err);
      await stopSharing();
    }
  };

  const stopSharing = React.useCallback(async () => {
    if (!room) return;

    try {
      const screenPub = room.localParticipant.getTrackPublication(
        Track.Source.ScreenShare
      );
      const audioPub = room.localParticipant.getTrackPublication(
        Track.Source.ScreenShareAudio
      );

      const screenTrack = screenPub?.track;
      const audioTrack = audioPub?.track;

      if (screenTrack) {
        await room.localParticipant.unpublishTrack(screenTrack);
        screenTrack.stop();
      }

      if (audioTrack) {
        await room.localParticipant.unpublishTrack(audioTrack);
        audioTrack.stop();
      }

      if (!socket || !user?.id) return;
      
      const stopScreenSharingMessage = JSON.stringify({
        type: 'stop-screen-sharing',
        meta: {
          channelId: activeCall.chatId,
          userId: user.id,
          participantIdentity: activeCall.name || user.name || user.id,
        },
      });
      socket.send(stopScreenSharingMessage);
    } catch (err) {
      console.error('Error stopping screen share:', err);
    }
  }, [room]);

  if (!isSharing && !docked) return; // if not sharing and not inside video channel

  return (
    <>
      <ActionTooltip label="Демонстрация экрана/окна" side="top">
        <button
          onClick={isSharing ? stopSharing : handleOpen}
          className={cn(docked ? "h-9" : "h-6", "px-2 py-1 rounded-lg")}
        >
          {isSharing ? (
            <ScreenShareOffIcon size={cn(docked ? 24 : 16)} className={'text-red-500'} />
          ) : (
            <ScreenShareIcon size={cn(docked ? 24 : 16)} />
          )}
        </button>
      </ActionTooltip>
      <SourceSelectorModal
        open={isModalOpen}
        onClose={handleClose}
        onSelect={handleSelect}
      />
    </>
  );
};
